import React, { useState } from 'react';
import { Organization } from '@/types';
import { useModals } from '@/contexts/ModalContext';

interface OrganizationsTabProps {
    organizations: Organization[];
    isDark: boolean;
    onDeleteOrganization: (orgId: number) => Promise<void>;
}

const OrganizationsTab: React.FC<OrganizationsTabProps> = ({
    organizations,
    isDark,
    onDeleteOrganization
}) => {
    const { openCreateOrgModal } = useModals();
    const [deletingOrgId, setDeletingOrgId] = useState<number | null>(null);
    
    const handleDelete = async (org: Organization) => {
        if (!confirm(`Are you sure you want to delete "${org.name}"? This will also delete all teams and memberships within the organization.`)) {
            return;
        }
        
        try {
            setDeletingOrgId(org.id);
            await onDeleteOrganization(org.id);
        } finally {
            setDeletingOrgId(null);
        }
    };
    
    return (
    <div className="cgm-card overflow-hidden">
        <div className="p-6 border-b border-(--card-border)">
            <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold cgm-text-primary">Organizations</h3>
                <button
                    onClick={openCreateOrgModal}
                    className="cgm-button px-6 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105"
                >
                    Create Organization
                </button>
            </div>
        </div>
        <div className="cgm-table-wrap">
            <table className="cgm-table cgm-table-striped">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Created</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {organizations.map((org) => (
                        <tr key={org.id}>
                            <td>
                                <div className="text-sm font-medium cgm-text-primary">{org.name}</div>
                            </td>
                            <td>
                                <div className="text-sm cgm-text-secondary">{org.description || 'No description'}</div>
                            </td>
                            <td>
                                <div className="text-sm cgm-text-secondary">
                                    {new Date(org.created_at).toLocaleDateString()}
                                </div>
                            </td>
                            <td>
                                <button
                                    onClick={() => handleDelete(org)}
                                    disabled={deletingOrgId === org.id}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                        deletingOrgId === org.id
                                            ? 'bg-gray-400 cursor-not-allowed'
                                            : 'bg-red-600 hover:bg-red-700 cgm-text-tertiary transform hover:scale-105'
                                    }`}
                                >
                                    {deletingOrgId === org.id ? 'Deleting...' : 'Delete'}
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
    );
};

export default OrganizationsTab; 